/**
 * PageMeta
 * ────────
 * Per-route meta-tag setter. Dependency-free alternative to
 * react-helmet — directly mutates document.title and the relevant
 * meta / link tags via useEffect. Renders nothing.
 *
 * Use at the top of any page component:
 *   <PageMeta
 *     title="Shop | DAVIDMEDIA"
 *     description="…"
 *     url="https://www.davidmedia.de/shop"
 *     image="https://…/og-shop.jpg"   // optional, falls back to global
 *   />
 *
 * Set `noindex` on admin / internal routes (Aether, Builder) so search
 * engines that find the URL anyway get a hard 'do not index' signal:
 *   <PageMeta title="Aether" noindex />
 *
 * Pass `schema` for structured data (Product, FAQPage, …) — it is written
 * as its own JSON-LD block and removed again when the route changes.
 * hreflang and og:locale are derived from the current path automatically
 * (the <html lang> attribute is set per route in App.js / LanguageHandler).
 *
 * Note: this updates the DOM after React mounts, so Googlebot (which
 * runs JS) sees the per-page values. Static social-media crawlers
 * (WhatsApp, Twitter card scraper) only see the index.html defaults
 * until we do server-side rendering. Acceptable trade-off for now.
 */
import { useEffect } from 'react';
import SPRACH_PAARE from '../data/sprachPaare.json';

/* ─── DOM helpers ───────────────────────────────────────────────── */

function setMetaByName(name, content) {
  if (!content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setMetaByProp(prop, content) {
  if (!content) return;
  let el = document.querySelector(`meta[property="${prop}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', prop);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function removeAlternates() {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
}

function setAlternate(hreflang, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/* ─── Language alternates ───────────────────────────────────────────
   index.html used to ship one static hreflang pair (/ and /en) that stayed
   put on every route, so every subpage told Google its English twin was
   the homepage. Derive the real counterpart from the current path instead.
   Most routes are just "/en" + path; the pairs in data/sprachPaare.json
   aren't. scripts/prerender-meta.js reads the same list at build time. */
const ORIGIN = 'https://www.davidmedia.de';
const LOCALE_PAIRS = SPRACH_PAARE;

// English = /en and everything below /en/; everything else is German.
export const isEnglishPath = (pathname) => pathname === '/en' || pathname.startsWith('/en/');

export function deriveAlternates(pathname) {
  const p = pathname.replace(/\/+$/, '') || '/';
  const pair = LOCALE_PAIRS.find(([de, en]) => de === p || en === p);
  if (pair) return { de: pair[0], en: pair[1] };
  const de = p === '/en' ? '/' : p.startsWith('/en/') ? p.slice(3) : p;
  return { de, en: de === '/' ? '/en' : `/en${de}` };
}

/* ─── Component ─────────────────────────────────────────────────── */

export default function PageMeta({
  title,
  description,
  url,
  image,
  noindex = false,
  type = 'website',
  schema,
}) {
  // Structured data (Product, FAQPage, …). Kept in its own tagged script so
  // it can be swapped per route without touching the site-wide WebSite /
  // Organization blocks in index.html, and removed again on unmount.
  useEffect(() => {
    if (!schema) return undefined;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-pagemeta', '');
    el.textContent = JSON.stringify(schema);
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, [schema]);

  useEffect(() => {
    if (title) {
      document.title = title;
      setMetaByName('title', title);
      setMetaByProp('og:title', title);
      setMetaByProp('twitter:title', title);
    }
    if (description) {
      setMetaByName('description', description);
      setMetaByProp('og:description', description);
      setMetaByProp('twitter:description', description);
    }
    if (url) {
      setMetaByProp('og:url', url);
      setMetaByProp('twitter:url', url);
      setLink('canonical', url);
    } else if (noindex) {
      // noindex ohne eigene Adresse (404): keine canonical der vorigen Seite / der Startseite stehen lassen
      const alt = document.head.querySelector('link[rel="canonical"]');
      if (alt) alt.remove();
    }
    if (image) {
      setMetaByProp('og:image', image);
      setMetaByProp('twitter:image', image);
    }
    if (type) {
      setMetaByProp('og:type', type);
    }
    // Robots — explicit per-page directive. Aether / Builder set this
    // to 'noindex, nofollow' so the page is removed from index even
    // if a search engine ignores robots.txt.
    setMetaByName(
      'robots',
      noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large'
    );
    // Language of the share preview (WhatsApp, Facebook, …) per route.
    setMetaByProp('og:locale', isEnglishPath(window.location.pathname) ? 'en_US' : 'de_DE');
    // Point hreflang at this page's actual counterpart, not the homepage.
    // noindex pages (404, account, Aether) get none at all — otherwise
    // the previous route's links would stay in the head.
    if (!noindex) {
      const alt = deriveAlternates(window.location.pathname);
      setAlternate('de', ORIGIN + alt.de);
      setAlternate('en', ORIGIN + alt.en);
      setAlternate('x-default', ORIGIN + alt.de);
    } else {
      removeAlternates();
    }
  }, [title, description, url, image, noindex, type]);

  return null;
}
